import { useUser } from "@dashboard/auth";
import { categoryListUrl } from "@dashboard/categories/urls";
import { checkoutListUrl } from "@dashboard/checkouts/urls";
import { cmsPagesListUrl } from "@dashboard/cmsPages/urls";
import { collectionListUrl } from "@dashboard/collections/urls";
import { useAnalyticsMode } from "@dashboard/components/AnalyticsMode/AnalyticsModeProvider";
import { configurationMenuUrl } from "@dashboard/configuration";
import { getConfigMenuItemsPermissions } from "@dashboard/configuration/utils";
import { customerListUrl } from "@dashboard/customers/urls";
import { saleListUrl, voucherListUrl } from "@dashboard/discounts/urls";
import {
  dealerLocatorUrl,
  fulfillmentWindowUrl,
  googleAppsUrl,
  shippingEstimatorUrl,
  tieredPricingUrl,
} from "@dashboard/envSettings/urls";
import { ExtensionsPaths, ExtensionsUrls } from "@dashboard/extensions/urls";
import useCheckoutTermsPage from "@dashboard/extensions/views/InstalledExtensions/hooks/useCheckoutTermsPage";
import { useFlag } from "@dashboard/featureFlags";
import { giftCardListUrl } from "@dashboard/giftCards/urls";
import {
  AppTypeEnum,
  PermissionEnum,
  useInstalledAppsListQuery,
} from "@dashboard/graphql";
import { ConfigurationIcon } from "@dashboard/icons/Configuration";
import { CustomersIcon } from "@dashboard/icons/Customers";
import { DiscountsIcon } from "@dashboard/icons/Discounts";
import ModelingIcon from "@dashboard/icons/Modeling";
import { OrdersIcon } from "@dashboard/icons/Orders";
import { ProductsIcon } from "@dashboard/icons/Products";
import TaxesIcon from "@dashboard/icons/Taxes";
import { inquiryFormsListUrl } from "@dashboard/inquiryForms/urls";
import { commonMessages, sectionNames } from "@dashboard/intl";
import { pageListPath } from "@dashboard/modeling/urls";
import { pageTypeListUrl } from "@dashboard/modelTypes/urls";
import { orderDraftListUrl, orderListUrl } from "@dashboard/orders/urls";
import { pagesListUrl } from "@dashboard/pages/urls";
import { productListUrl } from "@dashboard/products/urls";
import { staffListUrl } from "@dashboard/staff/urls";
import { menuListUrl } from "@dashboard/structures/urls";
import {
  wsmComplianceListPath,
  wsmFeeListPath,
  wsmOptionSetListPath,
} from "@dashboard/wsmCompose/urls";
import { wsmDealerGroupListPath } from "@dashboard/wsmDealer/urls";
import { wsmFitmentSchemaListPath, wsmFitmentSectionUrlName } from "@dashboard/wsmFitment/urls";
import { wsmKitListPath } from "@dashboard/wsmKits/urls";
import { wsmSeriesListPath } from "@dashboard/wsmSeries/urls";
import { Box, SearchIcon } from "@saleor/macaw-ui-next";
import {
  ArrowDownUp,
  BadgeDollarSign,
  BotIcon,
  ChartAreaIcon,
  Chrome,
  CreditCard,
  FileText,
  HomeIcon,
  Mail,
  Megaphone,
  Package,
  Rocket,
  ShieldAlert,
  Star,
  TruckIcon,
} from "lucide-react";
import React from "react";
import { useIntl } from "react-intl";

import { SidebarMenuItem } from "../types";

/**
 * Private metadata key external apps use to declare where they should appear
 * in the Integrations menu. Value is a JSON string: {"category": "<CATEGORY>", "label": "<Label>"}.
 */
export const MENU_METADATA_KEY = "wsm.dashboard.menu";

/**
 * Only these categories may be declared by an app's `wsm.dashboard.menu` metadata.
 * `MenuCategory` is derived from this array, so it's the single source of truth —
 * add/remove a category here only, never in a separate type declaration.
 */
export const VALID_MENU_CATEGORIES = [
  "SHIPPING",
  "TAX",
  "GOOGLE",
  "PAYMENTS",
  "EMAILS",
  "IMPORTS_SYNCING",
  "FRAUD_DETECTION",
  "REVIEWS",
  "MARKETING",
  "CATALOG",
  "ORDERS",
] as const;

export type MenuCategory = (typeof VALID_MENU_CATEGORIES)[number];

export function isValidMenuCategory(value: string): value is MenuCategory {
  return (VALID_MENU_CATEGORIES as readonly string[]).includes(value);
}

export interface AppMenuAssignment {
  category: MenuCategory;
  label: string;
}

/**
 * Reads and validates an app's `wsm.dashboard.menu` metadata.
 * Uses the public `metadata` field (not `privateMetadata`) since this value is
 * non-sensitive UI placement data and `privateMetadata` requires MANAGE_APPS,
 * which most staff viewing the sidebar won't have.
 * Returns null (and logs an error) for missing/malformed/unrecognized declarations,
 * so a misconfigured app is dropped from the menu instead of breaking it.
 */
export function getAppMenuAssignment(app: {
  name: string;
  metadata: ReadonlyArray<{ key: string; value: string }>;
}): AppMenuAssignment | null {
  const entry = app.metadata.find(item => item.key === MENU_METADATA_KEY);

  if (!entry) {
    return null;
  }

  let parsed: { category?: string; label?: string };

  try {
    parsed = JSON.parse(entry.value);
  } catch {
     
    console.error(
      `[Integrations menu] App "${app.name}" has an invalid "${MENU_METADATA_KEY}" metadata value (not valid JSON): ${entry.value}`,
    );

    return null;
  }

  if (!parsed.category || !isValidMenuCategory(parsed.category)) {
     
    console.error(
      `[Integrations menu] App "${app.name}" declared an unknown category "${parsed.category}" in its "${MENU_METADATA_KEY}" metadata. Valid categories are: ${VALID_MENU_CATEGORIES.join(", ")}.`,
    );

    return null;
  }

  if (!parsed.label) {
     
    console.error(
      `[Integrations menu] App "${app.name}" is missing a "label" in its "${MENU_METADATA_KEY}" metadata.`,
    );

    return null;
  }

  return { category: parsed.category, label: parsed.label };
}

/** Internal apps that always appear in the Others submenu */
const INTERNAL_APPS = [
  { label: "Currency Settings", url: ExtensionsPaths.currencySettings, isTerms: false },
  { label: "Dealer Locator", url: dealerLocatorUrl(), isTerms: false },
  { label: "Captcha Settings", url: ExtensionsPaths.captchaSettings, isTerms: false },
  { label: "Tiered Pricing", url: tieredPricingUrl(), isTerms: false },
  {
    label: "Checkout Questions",
    url: "/third-party-integrations/checkout-question",
    isTerms: false,
  },
  { label: "Checkout Terms and Conditions", url: "", isTerms: true },
  {
    label: "Shipping Restrictions",
    url: "/third-party-integrations/shipping-restrictions",
    isTerms: false,
  },
] as const;

export function useMenuStructure() {
  const intl = useIntl();
  const { user } = useUser();
  const { enabled: showExtensions } = useFlag("extensions");
  const { isLive } = useAnalyticsMode();
  const { pageId: checkoutTermsPageId } = useCheckoutTermsPage();

  // Query ALL installed Saleor apps and plugins to populate sidebar categories
  const { data: installedAppsData } = useInstalledAppsListQuery({
    variables: { first: 100 },
  });

  const installedApps = React.useMemo(
    () =>
      (installedAppsData?.apps?.edges ?? []).map(edge => ({
        id: edge.node.id,
        name: edge.node.name ?? "",
        identifier: edge.node.identifier ?? "",
        isActive: edge.node.isActive ?? false,
        type: edge.node.type,
        metadata: edge.node.metadata ?? [],
      })),
    [installedAppsData],
  );

  // Helper function to get special URL for hardcoded apps only
  const getSpecialAppUrl = React.useCallback((appName: string): string | null => {
    const lowerName = appName.toLowerCase();

    // Handle truly hardcoded special paths
    if (lowerName.includes("will call")) {
      return ExtensionsPaths.willCallShipping;
    }

    if (lowerName.includes("currency")) {
      return ExtensionsPaths.currencySettings;
    }

    // Handle Google apps with special routing (but NOT Google Product Feed)
    if (
      (lowerName.includes("google merchant") || lowerName.includes("merchant center")) &&
      !lowerName.includes("feed") &&
      !lowerName.includes("product")
    ) {
      return googleAppsUrl();
    }

    if (
      (lowerName.includes("google search") || lowerName.includes("search console")) &&
      !lowerName.includes("feed") &&
      !lowerName.includes("product")
    ) {
      return googleAppsUrl();
    }

    // Handle User Emails - link to staff management
    if (
      lowerName.includes("user email") ||
      (lowerName.includes("staff") && lowerName.includes("email"))
    ) {
      return staffListUrl();
    }

    return null;
  }, []);

  const getExtensionUrl = (extension: (typeof installedApps)[0]) => {
    const specialUrl = getSpecialAppUrl(extension.name);

    if (specialUrl) {
      return specialUrl;
    }

    if (extension.type === AppTypeEnum.LOCAL) {
      return ExtensionsUrls.editCustomExtensionUrl(extension.id);
    }

    // For manifest apps (THIRDPARTY), always use edit URL for configuration access
    return ExtensionsUrls.resolveViewManifestExtensionUrl(extension.id);
  };

  const getIntegrationsSection = (): SidebarMenuItem => {
    const subcategories: SidebarMenuItem[] = [];

    // Static config per category: icon/label/always-visible internal links.
    // Which apps land in each category comes entirely from each app's
    // `wsm.dashboard.menu` private metadata, not from a hardcoded identifier list.
    const categoryConfig: Record<
      MenuCategory,
      { label: string; icon: React.ReactNode; alwaysShow: boolean; staticChildren: SidebarMenuItem[] }
    > = {
      SHIPPING: {
        label: "Shipping",
        icon: renderIcon(<TruckIcon size={20} />),
        alwaysShow: true,
        staticChildren: [
          {
            id: "app-shipping-will-call",
            label: "Will Call",
            url: ExtensionsPaths.willCallShipping,
            permissions: [] as PermissionEnum[],
            type: "item" as const,
          },
          {
            id: "app-shipping-estimator",
            label: "Shipping Estimator",
            url: shippingEstimatorUrl(),
            permissions: [] as PermissionEnum[],
            type: "item" as const,
          },
          {
            id: "app-shipping-fulfillment-window",
            label: "Fulfillment Window",
            url: fulfillmentWindowUrl(),
            permissions: [] as PermissionEnum[],
            type: "item" as const,
          },
        ],
      },
      TAX: {
        label: "Tax",
        icon: renderIcon(<TaxesIcon />),
        alwaysShow: false,
        staticChildren: [],
      },
      GOOGLE: {
        label: "Google",
        icon: renderIcon(<Chrome size={20} />),
        alwaysShow: true,
        staticChildren: [
          {
            id: "app-google-analytics-ads",
            label: "Google Integrations",
            url: googleAppsUrl(),
            permissions: [] as PermissionEnum[],
            type: "item" as const,
          },
        ],
      },
      PAYMENTS: {
        label: "Payments",
        icon: renderIcon(<CreditCard size={20} />),
        alwaysShow: false,
        staticChildren: [],
      },
      EMAILS: {
        label: "Emails",
        icon: renderIcon(<Mail size={20} />),
        alwaysShow: true,
        staticChildren: [
          {
            id: "app-email-inquiry-forms",
            label: "Inquiry Forms",
            url: ExtensionsPaths.smtp,
            permissions: [] as PermissionEnum[],
            type: "item" as const,
          },
        ],
      },
      IMPORTS_SYNCING: {
        label: "Imports & Syncing",
        icon: renderIcon(<ArrowDownUp size={20} />),
        alwaysShow: false,
        staticChildren: [],
      },
      FRAUD_DETECTION: {
        label: "Fraud Detection",
        icon: renderIcon(<ShieldAlert size={20} />),
        alwaysShow: false,
        staticChildren: [],
      },
      REVIEWS: {
        label: "Reviews",
        icon: renderIcon(<Star size={20} />),
        alwaysShow: false,
        staticChildren: [],
      },
      MARKETING: {
        label: "Marketing",
        icon: renderIcon(<Megaphone size={20} />),
        alwaysShow: false,
        staticChildren: [],
      },
      CATALOG: {
        label: "Catalog",
        icon: renderIcon(<ProductsIcon />),
        alwaysShow: false,
        staticChildren: [],
      },
      ORDERS: {
        label: "Orders",
        icon: renderIcon(<OrdersIcon />),
        alwaysShow: false,
        staticChildren: [],
      },
    };

    // Group installed apps by the category declared in their metadata.
    // Apps with missing/invalid/unrecognized metadata are dropped (see getAppMenuAssignment).
    const appsByCategory = installedApps.reduce(
      (
        acc: Record<MenuCategory, Array<{ app: (typeof installedApps)[0]; label: string }>>,
        app: (typeof installedApps)[0],
      ) => {
        const assignment = getAppMenuAssignment(app);

        if (!assignment) {
          return acc;
        }

        acc[assignment.category] = [...(acc[assignment.category] ?? []), { app, label: assignment.label }];

        return acc;
      },
      {} as Record<MenuCategory, Array<{ app: (typeof installedApps)[0]; label: string }>>,
    );

    VALID_MENU_CATEGORIES.forEach(category => {
      const config = categoryConfig[category];
      const dynamicChildren: SidebarMenuItem[] = (appsByCategory[category] ?? []).map(
        ({ app, label }) => ({
          id: `app-${category.toLowerCase().replace(/_/g, "-")}-${app.id}`,
          label,
          url: getExtensionUrl(app),
          permissions: [] as PermissionEnum[],
          type: "item" as const,
        }),
      );

      const children = [...config.staticChildren, ...dynamicChildren];

      if (children.length === 0 && !config.alwaysShow) {
        return;
      }

      subcategories.push({
        icon: config.icon,
        label: config.label,
        permissions: [] as PermissionEnum[],
        id: `integration-subcategory-${category.toLowerCase().replace(/_/g, "-")}`,
        type: "itemGroup" as const,
        url: undefined,
        children,
      });
    });

    // "Other" subcategory: fixed list of internal apps only
    const otherChildren: SidebarMenuItem[] = INTERNAL_APPS.map(appDef => {
      const url =
        appDef.isTerms && checkoutTermsPageId ? `/models/${checkoutTermsPageId}` : appDef.url;

      return {
        id: `app-other-${appDef.label.toLowerCase().replace(/\s+/g, "-")}`,
        label: appDef.label,
        url,
        permissions: [] as PermissionEnum[],
        type: "item" as const,
      };
    });

    subcategories.push({
      icon: renderIcon(<Package size={20} />),
      label: "Other",
      permissions: [] as PermissionEnum[],
      id: "integration-subcategory-other",
      type: "itemGroup" as const,
      url: undefined,
      children: otherChildren,
    });

    // Return single "Integrations" parent with all subcategories
    return {
      icon: renderIcon(<Package size={20} />),
      label: "Integrations",
      permissions: [] as PermissionEnum[],
      id: "integrations",
      type: "itemGroup" as const,
      url: undefined,
      children: subcategories,
    };
  };

  const menuItems: SidebarMenuItem[] = [
    ...(!isLive
      ? [
          {
            icon: renderIcon(<HomeIcon size={20} />),
            label: "Home (Demo)",
            id: "home",
            url: "/",
            type: "item",
          } as SidebarMenuItem,
        ]
      : []),
    {
      icon: renderIcon(<ChartAreaIcon size={20} />),
      label: "Analytics",
      id: "analytics",
      url: "/analytics",
      type: "item",
    },
    {
      icon: renderIcon(<Rocket size={20} />),
      label: "Setup Guide",
      id: "store-setup",
      url: "/wizard",
      type: "item",
    },
    ...(!isLive
      ? ([
          {
            icon: renderIcon(<BotIcon size={20} />),
            label: "WSM AI",
            id: "wsm-ai",
            url: "/wsm-ai",
            type: "item",
          } as SidebarMenuItem,
        ] as SidebarMenuItem[])
      : []),
    {
      icon: renderIcon(<SearchIcon />),
      label: (
        <Box display="flex" alignItems="center" gap={2}>
          {intl.formatMessage(sectionNames.search)}
        </Box>
      ),
      id: "search",
      url: "/search",
      permissions: [
        PermissionEnum.MANAGE_PRODUCTS,
        PermissionEnum.MANAGE_PAGES,
        PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES,
        PermissionEnum.MANAGE_ORDERS,
      ],
      type: "item",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.products),
          id: "products",
          url: productListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.categories),
          id: "categories",
          url: categoryListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.collections),
          id: "collections",
          url: collectionListUrl(),
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.wsmProductSeries),
          id: "wsm-product-series",
          url: wsmSeriesListPath,
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.wsmBundlesAndKits),
          id: "wsm-kits",
          url: wsmKitListPath,
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.giftCards),
          id: "giftCards",
          url: giftCardListUrl(),
          permissions: [PermissionEnum.MANAGE_GIFT_CARD],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.fitmentManagement),
          id: "fitmentManagement",
          url: wsmFitmentSchemaListPath,
          // Axes and qualifiers are this section too, not only schemas.
          matchUrls: [wsmFitmentSectionUrlName],
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.wsmOptionSets),
          id: "wsm-option-sets",
          url: wsmOptionSetListPath,
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.wsmFees),
          id: "wsm-fees",
          url: wsmFeeListPath,
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.wsmCompliance),
          id: "wsm-compliance",
          url: wsmComplianceListPath,
          permissions: [PermissionEnum.MANAGE_PRODUCTS],
          type: "item",
        },
      ],
      icon: renderIcon(<ProductsIcon />),
      url: productListUrl(),
      label: intl.formatMessage(sectionNames.catalog),
      permissions: [PermissionEnum.MANAGE_GIFT_CARD, PermissionEnum.MANAGE_PRODUCTS],
      id: "products",
      type: "itemGroup",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.orders),
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "orders",
          url: orderListUrl(),
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.draftOrders),
          permissions: [PermissionEnum.MANAGE_ORDERS],
          id: "order-drafts",
          url: orderDraftListUrl(),
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.paymentRecovery),
          // The parent group is gated on MANAGE_ORDERS, so staff need both this
          // and MANAGE_CHECKOUTS for the entry to appear.
          permissions: [PermissionEnum.MANAGE_CHECKOUTS],
          id: "payment-recovery",
          url: checkoutListUrl(),
          type: "item",
        },
      ],
      icon: renderIcon(<OrdersIcon />),
      label: intl.formatMessage(sectionNames.fulfillment),
      permissions: [PermissionEnum.MANAGE_ORDERS],
      id: "orders",
      url: orderListUrl(),
      type: "itemGroup",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.customers),
          // Distinct from the group's own "customers" id: the id is what the
          // menu's data-test-id is built from, and two elements answering to
          // menu-item-label-customers is ambiguous to a test and to a screen
          // reader alike. The Fulfillment group above names its children the
          // same way.
          id: "customer-list",
          url: customerListUrl(),
          permissions: [PermissionEnum.MANAGE_USERS],
          type: "item",
        },
        {
          // A dealer group is about who the customer is, not about the catalog,
          // so Dealer Pricing lives here rather than under Catalog.
          label: intl.formatMessage(sectionNames.wsmDealerPricing),
          id: "wsm-dealer-pricing",
          url: wsmDealerGroupListPath,
          permissions: [PermissionEnum.MANAGE_DISCOUNTS],
          type: "item",
        },
      ],
      icon: renderIcon(<CustomersIcon />),
      label: intl.formatMessage(sectionNames.customers),
      permissions: [PermissionEnum.MANAGE_USERS, PermissionEnum.MANAGE_DISCOUNTS],
      id: "customers",
      url: customerListUrl(),
      type: "itemGroup",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.promotions),
          id: "promotions",
          url: saleListUrl(),
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.vouchers),
          id: "vouchers",
          url: voucherListUrl(),
          type: "item",
        },
      ],
      icon: renderIcon(<DiscountsIcon />),
      label: intl.formatMessage(commonMessages.discounts),
      permissions: [PermissionEnum.MANAGE_DISCOUNTS],
      url: saleListUrl(),
      id: "discounts",
      type: "itemGroup",
    },
    {
      children: [
        {
          label: "Navigation",
          id: "navigation",
          url: menuListUrl(),
          permissions: [PermissionEnum.MANAGE_MENUS],
          type: "item",
        },
        {
          label: "Pages",
          id: "content-pages",
          url: pagesListUrl(),
          permissions: [PermissionEnum.MANAGE_PAGES],
          type: "item",
        },
        {
          label: "CMS Pages",
          id: "cms-pages",
          url: cmsPagesListUrl(),
          permissions: [PermissionEnum.MANAGE_PAGES],
          type: "item",
        },
        {
          label: "Inquiry Forms",
          id: "inquiry-forms",
          url: inquiryFormsListUrl(),
          permissions: [PermissionEnum.MANAGE_PAGES],
          type: "item",
        },
      ],
      icon: renderIcon(<FileText size={20} />),
      label: "Content Management",
      permissions: [PermissionEnum.MANAGE_PAGES, PermissionEnum.MANAGE_MENUS],
      id: "content",
      url: menuListUrl(),
      type: "itemGroup",
    },
    {
      children: [
        {
          label: intl.formatMessage(sectionNames.models),
          id: "models",
          url: pageListPath,
          permissions: [PermissionEnum.MANAGE_PAGES],
          type: "item",
        },
        {
          label: intl.formatMessage(sectionNames.modelTypes),
          id: "model-types",
          url: pageTypeListUrl(),
          permissions: [
            PermissionEnum.MANAGE_PAGES,
            PermissionEnum.MANAGE_PAGE_TYPES_AND_ATTRIBUTES,
          ],
          type: "item",
        },
      ],
      icon: renderIcon(<ModelingIcon />),
      label: intl.formatMessage(sectionNames.modeling),
      permissions: [PermissionEnum.MANAGE_PAGES],
      id: "modeling",
      url: pageListPath,
      type: "itemGroup",
    },
    // Inquiry Forms moved to Content Management dropdown per Issue #72
    // Single Integrations parent with nested subcategories
    ...(showExtensions ? [getIntegrationsSection()] : []),
    // App Settings removed per Issue #72
    {
      icon: renderIcon(<ConfigurationIcon />),
      label: intl.formatMessage(sectionNames.configuration),
      permissions: getConfigMenuItemsPermissions(intl),
      id: "configure",
      url: configurationMenuUrl,
      type: "item",
    },
    {
      icon: renderIcon(<BadgeDollarSign size={20} />),
      label: "Account Settings",
      id: "plan-billing",
      url: "/billing",
      type: "item",
    },
  ];

  const isMenuItemPermitted = (menuItem: SidebarMenuItem) => {
    const userPermissions = (user?.userPermissions || []).map(permission => permission.code);

    if (!menuItem?.permissions || menuItem?.permissions?.length < 1) {
      return true;
    }

    return menuItem.permissions.some(permission => userPermissions.includes(permission));
  };

  const getFilteredMenuItems = (menuItems: SidebarMenuItem[]) =>
    menuItems.filter(isMenuItemPermitted);

  return menuItems.reduce((resultItems: SidebarMenuItem[], menuItem: SidebarMenuItem) => {
    if (!isMenuItemPermitted(menuItem)) {
      return resultItems;
    }

    const { children } = menuItem;
    const filteredChildren = children ? getFilteredMenuItems(children) : undefined;

    return [...resultItems, { ...menuItem, children: filteredChildren }];
  }, []);
}

function renderIcon(icon: React.ReactNode) {
  return (
    <Box color="default2" __width={20} __height={20}>
      {icon}
    </Box>
  );
}
